import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import {
  productsApi,
  Product,
  ProductFilters,
  PaginatedProductResponse,
  ProductDetailResponse,
} from '@/api/customer/products';

// ============================================================================
// Query Keys
// ============================================================================

export const productKeys = {
  all: ['products'] as const,
  lists: () => [...productKeys.all, 'list'] as const,
  list: (filters: ProductFilters) => [...productKeys.lists(), filters] as const,
  details: () => [...productKeys.all, 'detail'] as const,
  detail: (slug: string) => [...productKeys.details(), slug] as const,
  related: (productId: string, limit: number) =>
    [...productKeys.all, 'related', productId, limit] as const,
  suggestions: (query: string, limit: number) =>
    [...productKeys.all, 'suggestions', query, limit] as const,
  featured: (limit: number) => [...productKeys.all, 'featured', limit] as const,
  options: () => [...productKeys.all, 'options'] as const,
};

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to fetch paginated products with filters and sorting
 * 
 * Features:
 * - 5 minute stale time for optimal caching
 * - keepPreviousData for smooth transitions between pages
 * - Automatic refetch on window focus disabled
 * - 2 retry attempts on failure
 * 
 * @param filters - Product filters including pagination, search, category, price range, etc.
 * @returns Query result with products data, loading state, and error handling
 */
export function useProducts(
  filters?: ProductFilters
): UseQueryResult<PaginatedProductResponse, Error> {
  return useQuery({
    queryKey: productKeys.list(filters || {}),
    queryFn: () => productsApi.fetchProducts(filters),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    refetchOnWindowFocus: false,
    retry: 2,
    placeholderData: (previousData) => previousData, // keepPreviousData replacement
  });
}

/**
 * Hook to fetch a single product by slug with related products
 * 
 * Features:
 * - 10 minute stale time for product details
 * - Only fetches when slug is provided
 * - Includes related products in response
 * 
 * @param slug - Product slug
 * @returns Query result with product detail data
 */
export function useProduct(
  slug: string | undefined
): UseQueryResult<ProductDetailResponse, Error> {
  return useQuery({
    queryKey: productKeys.detail(slug || ''),
    queryFn: () => productsApi.fetchProductBySlug(slug!),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!slug, // Only fetch when slug is provided
  });
}

/**
 * Hook to fetch related products for a given product ID
 * 
 * @param productId - Product ID
 * @param limit - Number of related products to fetch (default: 4)
 * @returns Query result with related products
 */
export function useRelatedProducts(
  productId: string | undefined,
  limit: number = 4
): UseQueryResult<Product[], Error> {
  return useQuery({
    queryKey: productKeys.related(productId || '', limit),
    queryFn: () => productsApi.fetchRelatedProducts(productId!, limit),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    enabled: !!productId, // Only fetch when productId is provided
  });
}

/**
 * Hook to fetch search suggestions with debouncing
 * 
 * Features:
 * - 300ms debounce on search query
 * - Only fetches when query has at least 2 characters
 * - 2 minute stale time for suggestions
 * 
 * @param query - Search query string
 * @param limit - Number of suggestions to fetch (default: 5)
 * @returns Query result with search suggestions
 */
export function useSearchSuggestions(
  query: string,
  limit: number = 5
): UseQueryResult<string[], Error> {
  // Debounce the search query by 300ms
  const [debouncedQuery] = useDebounce(query, 300);

  return useQuery({
    queryKey: productKeys.suggestions(debouncedQuery, limit),
    queryFn: () => productsApi.fetchSearchSuggestions(debouncedQuery, limit),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: debouncedQuery.length >= 2, // Only fetch when query has at least 2 characters
  });
}

/**
 * Hook to fetch featured products
 * 
 * @param limit - Number of featured products to fetch (default: 8)
 * @returns Query result with featured products
 */
export function useFeaturedProducts(
  limit: number = 8
): UseQueryResult<Product[], Error> {
  return useQuery({
    queryKey: productKeys.featured(limit),
    queryFn: () => productsApi.fetchFeaturedProducts(limit),
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });
}

// ============================================================================
// Prefetch Utilities
// ============================================================================

/**
 * Utility hook to get prefetch functions for products
 * 
 * @returns Object with prefetch functions for different product queries
 */
export function useProductPrefetch() {
  const queryClient = useQueryClient();

  return {
    /**
     * Prefetch products list with given filters
     */
    prefetchProducts: (filters: ProductFilters) => {
      return queryClient.prefetchQuery({
        queryKey: productKeys.list(filters),
        queryFn: () => productsApi.fetchProducts(filters),
        staleTime: 5 * 60 * 1000,
      });
    },

    /**
     * Prefetch product detail by slug
     */
    prefetchProduct: (slug: string) => {
      return queryClient.prefetchQuery({
        queryKey: productKeys.detail(slug),
        queryFn: () => productsApi.fetchProductBySlug(slug),
        staleTime: 10 * 60 * 1000,
      });
    },

    /**
     * Prefetch related products
     */
    prefetchRelatedProducts: (productId: string, limit: number = 4) => {
      return queryClient.prefetchQuery({
        queryKey: productKeys.related(productId, limit),
        queryFn: () => productsApi.fetchRelatedProducts(productId, limit),
        staleTime: 10 * 60 * 1000,
      });
    },
  };
}
